import React, { useState, useEffect } from 'react';
import { useTranslation } from "react-i18next";
import { Card, Typography, Button, Table, message } from 'antd';
import { MagnifyingGlass, SignOut } from '@phosphor-icons/react';
import styled from 'styled-components';
import { ImpersonationModal } from '../molecules/impersonation-modal/ImpersonationModal';
import { getAllUsers, logoutDeveloper } from '../../service/authService';

const { Title } = Typography;

const Container = styled.div`
  height: 100vh;
  overflow: hidden;
  background: linear-gradient(135deg, #f5f3ff 0%, #ede9fe 100%);
  padding: 48px 24px;
  display: flex;
  flex-direction: column;
`;

const ContentCard = styled(Card)`
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
  box-shadow: 0 8px 32px rgba(79, 70, 229, 0.12);
  border-radius: 12px;
  display: flex;
  flex-direction: column;
  min-height: 0;
  flex: 1;

  .ant-card-body {
    display: flex;
    flex-direction: column;
    min-height: 0;
    flex: 1;
    overflow: hidden;
  }
`;

const HeaderContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
  gap: 16px;

  @media (max-width: 576px) {
    flex-direction: column;
    align-items: flex-start;
  }
`;

export const DeveloperPanel: React.FC = () => {
  const { t } = useTranslation();
  const [modalOpen, setModalOpen] = useState(false);
  const [users, setUsers] = useState<any[]>([]);
  const [totalUsers, setTotalUsers] = useState<number>(0);
  const [loading, setLoading] = useState(true);
  const [loggingOut, setLoggingOut] = useState(false);

  const handleLogout = async () => {
    setLoggingOut(true);
    try {
      await logoutDeveloper();
    } catch {
      // logoutDeveloper already handles cookie cleanup
    } finally {
      window.location.reload();
    }
  };

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        setLoading(true);
        const data = await getAllUsers(1, 100);
        setUsers(data.users);
        setTotalUsers(data.pagination.total);
      } catch (error) {
        console.error("Failed to load users", error);
        message.error(t("developerPanel.fetchUsersError"));
      } finally {
        setLoading(false);
      }
    };
    fetchUsers();
  }, []);

  const columns = [
    {
      title: t("developerPanel.columnName"),
      key: 'name',
      render: (_: any, record: any) => `${record.firstName} ${record.lastName}`
    },
    {
      title: 'Email',
      dataIndex: 'email',
      key: 'email',
    },
    {
      title: t("developerPanel.columnCompany"),
      dataIndex: 'companyName',
      key: 'companyName',
    },
    {
      title: t("developerPanel.columnProductsCount"),
      dataIndex: 'productsCount',
      key: 'productsCount',
    },
    {
      title: t("developerPanel.columnServicesCount"),
      dataIndex: 'servicesCount',
      key: 'servicesCount',
    },
    {
      title: 'Activity',
      key: 'activity',
      render: (_: any, record: any) => {
        const fmt = (date: string | null) => date
          ? new Intl.DateTimeFormat('pl-PL', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }).format(new Date(date))
          : t("developerPanel.noData");
        return (
          <div>
            <div>First: {fmt(record.createdAt)}</div>
            <div>Last: {fmt(record.lastLoginAt)}</div>
          </div>
        );
      }
    }
  ];

  return (
    <>
      <ImpersonationModal open={modalOpen} onClose={() => setModalOpen(false)} />

      <Container>
        <ContentCard>
          <HeaderContainer>
            <Title level={3} style={{ margin: 0 }}>
              {t("developerPanel.usersCount", { count: totalUsers })}
            </Title>
            <div style={{ display: 'flex', gap: 12 }}>
              <Button
                type="primary"
                size="middle"
                icon={<MagnifyingGlass size={18} />}
                onClick={() => setModalOpen(true)}
              >
                {t("developerPanel.switchCompany")}
              </Button>
              <Button
                danger
                size="middle"
                icon={<SignOut size={18} />}
                onClick={handleLogout}
                loading={loggingOut}
              >
                {t("developerPanel.logout")}
              </Button>
            </div>
          </HeaderContainer>

          <Table
            dataSource={users}
            columns={columns}
            rowKey="id"
            loading={loading}
            pagination={{ pageSize: 15 }}
            scroll={{ x: 800, y: "calc(100vh - 280px)" }}
            style={{ flex: 1, minHeight: 0 }}
          />
        </ContentCard>
      </Container>
    </>
  );
};
